import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, GraduationCap, BookOpen, Users, Building2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: "student", label: "Student", icon: GraduationCap, description: "I'm preparing for BECE exams", color: "border-blue-400 bg-blue-50 hover:bg-blue-100 text-blue-700" },
  { value: "teacher", label: "Teacher", icon: BookOpen, description: "I teach and want resources for my students", color: "border-emerald-400 bg-emerald-50 hover:bg-emerald-100 text-emerald-700" },
  { value: "parent", label: "Parent", icon: Users, description: "I'm supporting my child's education", color: "border-purple-400 bg-purple-50 hover:bg-purple-100 text-purple-700" },
  { value: "school_admin", label: "School Administrator", icon: Building2, description: "I manage a school and need bulk resources", color: "border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-700" },
];

export default function RoleSelectionPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!selectedRole) {
      toast.error("Please select your role to continue");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/auth/set-role`, { user_type: selectedRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Welcome! Your account is all set.");
      // Force refresh user data
      window.location.href = "/dashboard";
    } catch (error) {
      console.error("Set role error:", error);
      toast.error(error.response?.data?.detail || "Failed to set role");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl overflow-hidden">
            <img src="/logo.png" alt="Great Messiah Publication" className="w-full h-full object-contain" />
          </div>
          <CardTitle className="font-heading text-2xl">Welcome{user?.name ? `, ${user.name}` : ''}!</CardTitle>
          <CardDescription className="text-base mt-1">
            Tell us about yourself so we can personalize your experience.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-2">
          <p className="text-sm font-medium text-gray-600 mb-4">I am a...</p>
          <div className="grid grid-cols-1 gap-3">
            {ROLES.map(({ value, label, icon: Icon, description, color }) => (
              <button
                key={value}
                onClick={() => setSelectedRole(value)}
                className={`flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all duration-200 ${
                  selectedRole === value
                    ? `${color} ring-2 ring-offset-1 ring-primary/30 scale-[1.02] shadow-md`
                    : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                }`}
                data-testid={`role-option-${value}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                  selectedRole === value ? color.split(" ")[1] : "bg-gray-100"
                }`}>
                  <Icon className={`w-6 h-6 ${selectedRole === value ? color.split(" ").pop() : "text-gray-400"}`} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{label}</p>
                  <p className="text-sm text-gray-500">{description}</p>
                </div>
              </button>
            ))}
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!selectedRole || loading}
            className="w-full h-12 mt-6 rounded-xl bg-primary hover:bg-primary/90 text-base font-semibold"
            data-testid="role-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Setting up your account...
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
